"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { client } from "@/sanity/lib/client";
import { Calendar, MapPin } from "lucide-react";

const getInYourCityEventsQuery = `
  *[_type == "inYourCityEvents"] | order(index asc) {
    _id,
    index,
    title,
    cityName,
    location,
    redirectUrl,
    date,
    image {
      "url": asset->url,
      alt,
      caption
    }
  }
`;

interface InYourCityEvent {
  _id: string;
  title: string;
  cityName: string;
  location: string;
  redirectUrl: string;
  date: string;
  image: {
    url: string;
    alt: string;
    caption: string;
  };
}

const CityEvents = () => {
  const [events, setEvents] = useState<InYourCityEvent[]>([]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await client.fetch(getInYourCityEventsQuery);
        setEvents(data);
      } catch (error) {
        console.error("Error fetching city events:", error);
      }
    };

    fetchEvents();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 xl:gap-[48px] my-10 sm:my-14 lg:my-[92px]">
        {events?.map((event) => (
          <div
            key={event._id}
            className="rounded-[16px] lg:rounded-[20px] group"
          >
            <div className="relative z-10 shadow-[0px_4px_12px_0px_#00000040] rounded-l-[20px] rounded-tl-[20px] overflow-hidden">
              <Image
                src={event.image.url}
                alt={event.image.alt}
                width={500}
                height={778}
                className="
                  w-full 
                  object-cover 
                  h-[250px] 
                  sm:h-[300px] 
                  lg:h-full
                  rounded-l-[20px] rounded-tl-[20px]
                  group-hover:scale-105 duration-300 ease-in-out
                "
              />
              <div
                className="
                  absolute right-0 bottom-0 z-20 
                  bg-[#FFFFFFCC] 
                  rounded-tl-[16px] lg:rounded-tl-[24px]
                  p-4 sm:p-5 xl:p-[26px]
                  w-[90%] sm:w-[85%] lg:w-[80%]
                "
              >
                <p className="text-base sm:text-lg lg:text-xl xl:text-2xl font-semibold ff_poppins">
                  {event.title}
                </p>

                <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4 lg:mt-5">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 text-black inline-block">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-black" />
                  </span>
                  <p className="text-sm sm:text-base lg:text-xl font-semibold ff_poppins">
                    {event.date}
                  </p>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4 lg:mt-5">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 inline-block">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                  <p className="text-sm sm:text-base lg:text-xl font-semibold ff_poppins">
                    {event.location}
                  </p>
                </div>
              </div>
            </div>

            <Link
              href={event.redirectUrl}
              className="
                text-white text-center font-semibold ff_poppins 
                text-lg sm:text-xl lg:text-2xl
                pb-4 pt-6 sm:pb-5 sm:pt-7 lg:pb-6 lg:pt-8
                -mt-3 lg:-mt-4
                bg-primary-gradient 
                inline-block w-full 
                rounded-b-[16px] lg:rounded-b-[20px]
              "
            >
              Know More
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CityEvents;
